import { toTimestamp, TsDate, TsParam, UnixTimestamp } from "./timestamps.tools.definitions";

//#region Px Converters

export enum WorkDateType {
    START = 1,
    FINISH = 2
}

/** work calender helper that makes "not worked hours" hidden */
export interface TimelineWorkCalendarHelper {
    /**
     * return the worked seconds between 2 dates
     */
    getWorkedSecondsBetween(from: TsParam, to: TsParam): number;

    /**
     * add worked seconds to a date and return a start or finish date
     */
    addWorkedSeconds(from: TsParam, seconds: number, targetDateType: WorkDateType): TsDate;

    /** Return the from date adjusted to a workable date */
    getWorkableDate(from: TsParam, dateType: WorkDateType): TsDate;
}

/**
 * Convert a date to an X coordinate from a context
 * @param context Context of the parent
 * @param context.startTs Start date
 * @param context.finishTs Finish date
 * @param context.width Parent's width
 * @param context.beginX Parent X offset
 * @param context.workCalendarHelper work calender helper that makes "not worked hours" hidden
 * @param parameters Parameters
 * @param parameters.ts UnixTimestamp to convert into px
 * @returns coordinate in px
 */
export function timestampToPx(context: {
    startTs: UnixTimestamp,
    finishTs: UnixTimestamp,
    width: number,
    beginX: number,
    workCalendarHelper?: TimelineWorkCalendarHelper
}, parameters: { ts: UnixTimestamp }): number {
    let totalDuration: number, durationUntilTs: number;
    if (context.workCalendarHelper) {
        totalDuration = context.workCalendarHelper.getWorkedSecondsBetween(context.startTs, context.finishTs);
        durationUntilTs = context.workCalendarHelper.getWorkedSecondsBetween(context.startTs, parameters.ts);
    } else {
        totalDuration = context.finishTs - context.startTs;
        durationUntilTs = parameters.ts - context.startTs;
    }
    if (totalDuration === 0) {
        return context.beginX;
    }
    // Compute X position of input timestamp
    return context.beginX + durationUntilTs * context.width / totalDuration;
}

/**
 * Convert an X coordinate to a timestamp from a context
 * @param context Context of the parent
 * @param context.startTs Start date
 * @param context.finishTs Finish date
 * @param context.width Parent's width
 * @param context.beginX Parent X offset
 * @param context.workCalendarHelper work calender helper that makes "not worked hours" hidden
 * @param parameters Parameters
 * @param parameters.px  Px to convert into date
 * @param parameters.targetDateType The expected day type (start date or finish date)
 * @returns the UnixTimestamp corresponding to px in input
 */
export function pxToTimestamp(context: {
    startTs: UnixTimestamp,
    finishTs: UnixTimestamp,
    width: number,
    beginX: number,
    workCalendarHelper?: TimelineWorkCalendarHelper
}, parameters: { px: number, targetDateType: WorkDateType }): UnixTimestamp {
    if (context.width <= 0) { return context.startTs; }
    let totalDuration: number;
    if (context.workCalendarHelper) {
        totalDuration = context.workCalendarHelper.getWorkedSecondsBetween(context.startTs, context.finishTs);
    } else {
        totalDuration = context.finishTs - context.startTs;
    }
    // Compute corresponding duration of input px
    const seconds = (parameters.px - context.beginX) * totalDuration / context.width;

    if (context.workCalendarHelper) {
        return context.workCalendarHelper.addWorkedSeconds(context.startTs, seconds, parameters.targetDateType).uts;
    } else {
        return toTimestamp(Math.round(context.startTs + seconds));
    }
}
//#endregion
